import {
  browserPrivacyControlsRepository,
  DISCORD_IMAGE_NOTICE_VERSION,
  discordImageConsentDecision,
  TELEMETRY_NOTICE_VERSION,
  telemetryConsentDecision,
} from "../privacy-controls.js";
import { translate } from "../account/identity-view-primitives.js";
import {
  decodeTelemetrySelection,
  telemetrySelectionPreview,
} from "../telemetry-selection-contract.js";

export const CONSENT_CENTER_TAG = "fishy-consent-center";

const MARKERS = Object.freeze({
  telemetry: "[data-privacy-telemetry-consent-toggle]",
  telemetryStatus: "[data-privacy-telemetry-consent-status]",
  traceTelemetry: "[data-privacy-trace-telemetry-toggle]",
  traceTelemetryStatus: "[data-privacy-trace-telemetry-status]",
  rumTelemetry: "[data-privacy-rum-telemetry-toggle]",
  rumTelemetryStatus: "[data-privacy-rum-telemetry-status]",
  telemetryField: "[data-privacy-telemetry-field]",
  telemetryApproval: "[data-privacy-telemetry-approval]",
  telemetryApprovalStatus: "[data-privacy-telemetry-approval-status]",
  discordImages: "[data-privacy-discord-images-toggle]",
  discordImagesStatus: "[data-privacy-discord-images-status]",
});

export function accountPrivacyUrl(locationLike) {
  return String(locationLike?.pathname ?? "").startsWith("/de-DE/")
    ? `${locationLike.origin}/de-DE/konto/datenschutz/`
    : `${locationLike.origin}/account/privacy/`;
}

export function isAccountPrivacyPath(pathname) {
  return ["/account/privacy/", "/de-DE/konto/datenschutz/"].includes(
    String(pathname ?? ""),
  );
}

const TRANSACTIONAL_IDENTITY_PATHS = new Set([
  "/account/identity-complete/",
  "/de-DE/konto/identitaet-abschliessen/",
  "/desktop-auth/",
]);

export function isTransactionalIdentityPath(pathname) {
  return TRANSACTIONAL_IDENTITY_PATHS.has(String(pathname ?? ""));
}

function setStatus(documentRef, selector, text) {
  for (const node of documentRef?.querySelectorAll?.(selector) ?? []) {
    node.textContent = text;
  }
}

function setClassName(documentRef, selector, className) {
  for (const node of documentRef?.querySelectorAll?.(selector) ?? []) {
    node.className = className;
  }
}

function setToggle(documentRef, selector, { checked, disabled }) {
  for (const node of documentRef?.querySelectorAll?.(selector) ?? []) {
    node.checked = checked;
    node.disabled = disabled;
    node.setAttribute?.("aria-checked", String(checked));
  }
}

function privacyMutable(state) {
  return ["missing", "ready"].includes(state?.kind);
}

function currentDecision(receipt, noticeVersion) {
  return ["granted", "denied"].includes(receipt?.decision?.kind)
    && receipt.decision.noticeVersion === noticeVersion;
}


export function optionalConsentChoicesComplete(privacyState) {
  return privacyState?.kind === "ready"
    && currentDecision(privacyState.discordImageConsent, DISCORD_IMAGE_NOTICE_VERSION);
}

export function consentBannerSelections(privacyState) {
  return Object.freeze({
    discordImages: discordImageConsentDecision(privacyState).effectiveEnabled === true,
  });
}

export function telemetryActivationChanged(previousState, nextState) {
  return telemetryConsentDecision(previousState).effectiveEnabled
    !== telemetryConsentDecision(nextState).effectiveEnabled;
}

export function consentCenterPresentation({
  dismissed = false,
  privacyPath = false,
  transactionalPath = false,
  privacyState,
} = {}) {
  if (
    transactionalPath
    || !privacyMutable(privacyState)
  ) {
    return "hidden";
  }
  return !dismissed
    && !privacyPath
    && !optionalConsentChoicesComplete(privacyState)
    ? "banner"
    : "hidden";
}

function checkbox(id, label, detail) {
  const wrapper = document.createElement("label");
  wrapper.className = "flex cursor-pointer items-start gap-3 rounded-box px-2 py-2 hover:bg-base-200/60";
  const input = document.createElement("input");
  input.id = id;
  input.type = "checkbox";
  input.className = "checkbox checkbox-sm mt-0.5";
  const copy = document.createElement("span");
  copy.className = "min-w-0";
  const title = document.createElement("span");
  title.className = "block text-sm font-medium";
  title.textContent = label;
  const description = document.createElement("span");
  description.className = "mt-0.5 block text-xs leading-5 text-base-content/60";
  description.textContent = detail;
  copy.append(title, description);
  wrapper.append(input, copy);
  return { wrapper, input };
}

const HTMLElementBase = globalThis.HTMLElement ?? class {};

export class FishyConsentCenter extends HTMLElementBase {
  constructor() {
    super();
    this._connected = false;
    this._dismissed = false;
    this._draft = null;
    this._projectingTelemetrySignals = false;
    this._privacyState = Object.freeze({ kind: "uninitialized" });
    this._privacyRepositoryProvider = browserPrivacyControlsRepository;
    this._privacyRepository = null;
    this._unsubscribePrivacy = null;
    this._pending = false;
    this._onChange = this._onChange.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    if (isTransactionalIdentityPath(globalThis.location?.pathname)) {
      this.hidden = true;
      return;
    }
    this.addEventListener("click", this._onClick);
    globalThis.document?.addEventListener?.("change", this._onChange);
    try {
      this._privacyRepository = this._privacyRepositoryProvider();
      this._unsubscribePrivacy = this._privacyRepository.subscribe((state) => {
        this._privacyState = state;
        this._project();
      });
      void this._privacyRepository.load();
    } catch (_error) {
      this.hidden = true;
    }
  }

  disconnectedCallback() {
    this._connected = false;
    this.removeEventListener("click", this._onClick);
    globalThis.document?.removeEventListener?.("change", this._onChange);
    this._unsubscribePrivacy?.();
    this._unsubscribePrivacy = null;
  }

  _project() {
    if (this._pending) {
      for (const selector of [
        MARKERS.telemetry,
        MARKERS.traceTelemetry,
        MARKERS.rumTelemetry,
        MARKERS.telemetryField,
        MARKERS.telemetryApproval,
        MARKERS.discordImages,
      ]) {
        for (const node of document.querySelectorAll?.(selector) ?? []) {
          node.disabled = true;
        }
      }
      return;
    }
    const telemetry = telemetryConsentDecision(this._privacyState);
    const discord = discordImageConsentDecision(this._privacyState);
    const mutable = privacyMutable(this._privacyState) && !this._pending;
    const telemetryGranted = telemetry.consentGranted === true;
    const telemetryAvailable = telemetryGranted;
    setToggle(document, MARKERS.telemetry, {
      checked: telemetryGranted,
      disabled: !mutable,
    });
    setToggle(document, MARKERS.traceTelemetry, {
      checked: telemetry.selection.traces.enabled,
      disabled: !mutable || !telemetryAvailable,
    });
    setToggle(document, MARKERS.rumTelemetry, {
      checked: telemetry.selection.rum.enabled,
      disabled: !mutable || !telemetryAvailable,
    });
    for (const input of document.querySelectorAll?.(MARKERS.telemetryField) ?? []) {
      const signal = input.dataset.telemetrySignal;
      const field = input.dataset.telemetryField;
      input.checked = telemetry.selection?.[signal]?.fields?.[field] === true;
      input.disabled = !mutable || !telemetryAvailable;
    }
    setToggle(document, MARKERS.discordImages, {
      checked: discord.effectiveEnabled,
      disabled: !mutable,
    });
    setStatus(document, MARKERS.telemetryStatus, telemetryGranted
      ? translate("consent.telemetry.granted", "Allowed under the current telemetry notice.")
      : telemetry.choice === "disabled"
        ? translate("consent.telemetry.denied", "Not allowed.")
        : translate("consent.telemetry.unset", "No decision yet; telemetry remains off."));
    setStatus(document, MARKERS.traceTelemetryStatus, telemetry.tracesEnabled
      ? translate("consent.telemetry.traces_on", "Trace sending is enabled with the previewed fields.")
      : translate("consent.telemetry.traces_off", "Trace sending is off."));
    setStatus(document, MARKERS.rumTelemetryStatus, telemetry.rumEnabled
      ? translate("consent.telemetry.rum_on", "RUM sending is enabled with the previewed fields.")
      : translate("consent.telemetry.rum_off", "RUM sending is off."));
    setStatus(document, MARKERS.discordImagesStatus, discord.effectiveEnabled
      ? translate("consent.discord_images.granted", "Discord-hosted profile images may load.")
      : discord.choice === "denied"
        ? translate("consent.discord_images.denied", "Discord-hosted images stay blocked.")
        : translate("consent.discord_images.unset", "No decision yet; Discord-hosted images stay blocked."));
    for (const session of document.querySelectorAll?.("fishy-account-session") ?? []) {
      session.discordImagesAllowed = discord.effectiveEnabled;
    }
    this.projectTelemetrySelectionSignals();
    this._renderTelemetryApproval();
    this._renderBanner();
  }

  async initializeTelemetrySelectionSignals() {
    await this._privacyRepository?.load?.();
    this.projectTelemetrySelectionSignals();
  }

  projectTelemetrySelectionSignals() {
    const project = () => {
      if (!this._connected) return;
      const EventConstructor = this.ownerDocument?.defaultView?.Event ?? globalThis.Event;
      if (typeof EventConstructor !== "function") return;
      this._projectingTelemetrySignals = true;
      try {
        for (const input of document.querySelectorAll?.(
          `${MARKERS.traceTelemetry}, ${MARKERS.rumTelemetry}, ${MARKERS.telemetryField}`,
        ) ?? []) {
          input.dispatchEvent?.(new EventConstructor("input", { bubbles: true }));
        }
      } finally {
        this._projectingTelemetrySignals = false;
      }
    };
    if (typeof globalThis.queueMicrotask === "function") {
      globalThis.queueMicrotask(project);
    } else {
      project();
    }
  }

  _renderTelemetryApproval() {
    const telemetry = telemetryConsentDecision(this._privacyState);
    setToggle(document, MARKERS.telemetryApproval, {
      checked: telemetry.selectionApproved,
      disabled: !privacyMutable(this._privacyState)
        || telemetry.consentGranted !== true
        || this._pending,
    });
    setClassName(
      document,
      MARKERS.telemetryApprovalStatus,
      telemetry.selectionApproved
        ? "badge badge-success badge-soft badge-sm shrink-0"
        : "badge badge-warning badge-soft badge-sm shrink-0",
    );
    setStatus(
      document,
      MARKERS.telemetryApprovalStatus,
      telemetry.selectionApproved
        ? translate("consent.telemetry.selection_approved", "Approved for sending")
        : translate("consent.telemetry.selection_not_approved", "Not approved for sending"),
    );
  }

  telemetrySelectionPreviewJson(value) {
    return JSON.stringify(telemetrySelectionPreview(decodeTelemetrySelection(value)), null, 2);
  }

  stageTelemetrySelection(value) {
    if (this._pending || this._projectingTelemetrySignals) return;
    const selection = decodeTelemetrySelection(value);
    return this._applyPrivacyMutation(
      () => this._privacyRepository.setTelemetrySelection(selection),
    );
  }

  setTelemetrySelectionApproval(approved) {
    if (this._pending) return;
    return this._applyPrivacyMutation(
      () => this._privacyRepository.setTelemetrySelectionApproval(approved === true),
    );
  }

  toggleTelemetrySelectionApproval() {
    const telemetry = telemetryConsentDecision(this._privacyState);
    return this.setTelemetrySelectionApproval(!telemetry.selectionApproved);
  }

  _renderBanner() {
    if (this._pending) return;
    const presentation = consentCenterPresentation({
      dismissed: this._dismissed,
      privacyPath: isAccountPrivacyPath(globalThis.location?.pathname),
      transactionalPath: isTransactionalIdentityPath(globalThis.location?.pathname),
      privacyState: this._privacyState,
    });
    if (presentation === "hidden") {
      this.hidden = true;
      this.replaceChildren();
      return;
    }
    this.hidden = false;
    this._draft ??= consentBannerSelections(this._privacyState);
    const section = document.createElement("section");
    section.className = "card card-sm card-border pointer-events-auto bg-base-100 shadow-2xl";
    section.setAttribute("aria-labelledby", "fishystuff-consent-title");
    const body = document.createElement("div");
    body.className = "card-body gap-3";
    const heading = document.createElement("div");
    heading.className = "flex items-start justify-between gap-3";
    const copy = document.createElement("div");
    const title = document.createElement("h2");
    title.id = "fishystuff-consent-title";
    title.className = "font-semibold";
    title.textContent = translate("consent.title", "Optional features");
    const detail = document.createElement("p");
    detail.className = "mt-1 text-xs leading-5 text-base-content/65";
    detail.textContent = translate(
      "consent.detail",
      "FishyStuff works without these. Choose which external features may run.",
    );
    copy.append(title, detail);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "btn btn-ghost btn-square btn-xs";
    close.dataset.consentDismiss = "";
    close.setAttribute("aria-label", translate("consent.dismiss", "Decide later"));
    close.textContent = "×";
    heading.append(copy, close);

    const discord = checkbox(
      "consent-banner-discord-images",
      translate("consent.discord_images.title", "Discord profile images"),
      translate("consent.discord_images.detail", "Load a selected profile image directly from Discord's CDN."),
    );
    discord.input.dataset.consentDiscordImages = "";
    const selections = this._draft;
    discord.input.checked = selections.discordImages;

    const actions = document.createElement("div");
    actions.className = "card-actions items-center justify-between gap-2";
    const settings = document.createElement("a");
    settings.className = "btn btn-ghost btn-xs";
    settings.href = accountPrivacyUrl(globalThis.location);
    settings.textContent = translate("consent.settings", "Privacy settings");
    const choices = document.createElement("div");
    choices.className = "flex flex-wrap justify-end gap-2";
    const off = document.createElement("button");
    off.type = "button";
    off.className = "btn btn-ghost btn-sm";
    off.dataset.consentKeepOff = "";
    off.textContent = translate("consent.keep_off", "Keep optional features off");
    const save = document.createElement("button");
    save.type = "button";
    save.className = "btn btn-sm";
    save.dataset.consentSave = "";
    save.textContent = translate("consent.save", "Allow selected");
    choices.append(off, save);
    actions.append(settings, choices);
    body.append(heading, discord.wrapper, actions);
    section.append(body);
    this.replaceChildren(section);
  }

  _toast(message) {
    globalThis.__fishystuffToast?.error?.(message, { duration: Infinity });
  }

  _setPending(pending) {
    this._pending = pending;
    this.toggleAttribute("aria-busy", pending);
    for (const control of this.querySelectorAll?.("input, button") ?? []) {
      control.disabled = pending;
    }
    if (pending) this._project();
  }

  async _applyPrivacyMutation(mutation) {
    if (this._pending) return;
    this._setPending(true);
    try {
      const state = await mutation();
      if (state.kind !== "ready") throw new Error();
    } catch (_error) {
      this._toast(translate(
        "consent.save_failed",
        "Your privacy choices could not be saved. Try again.",
      ));
    } finally {
      this._setPending(false);
      this._project();
    }
  }

  async _saveChoices({ discordImages }) {
    if (this._pending) return;
    this._setPending(true);
    try {
      const state = await this._privacyRepository.setDiscordImageConsent(discordImages);
      if (state.kind !== "ready") throw new Error();
      this._dismissed = true;
      this._draft = null;
      this._project();
    } catch (_error) {
      this._toast(translate("consent.save_failed", "Your privacy choices could not be saved. Try again."));
    } finally {
      this._setPending(false);
      this._project();
    }
  }


  _onClick(event) {
    if (event.target?.closest?.("[data-consent-dismiss]")) {
      this._dismissed = true;
      this._draft = null;
      this._renderBanner();
      return;
    }
    const keepOff = event.target?.closest?.("[data-consent-keep-off]");
    const save = event.target?.closest?.("[data-consent-save]");
    if (!keepOff && !save) return;
    void this._saveChoices({
      discordImages: save
        ? this.querySelector("[data-consent-discord-images]")?.checked === true
        : false,
    });
  }

  _onChange(event) {
    if (
      this.contains(event.target)
      && event.target?.matches?.("[data-consent-discord-images]")
    ) {
      this._draft = Object.freeze({
        discordImages: event.target.checked === true,
      });
      return;
    }
    const telemetry = event.target?.closest?.(MARKERS.telemetry);
    if (telemetry) {
      void this._applyPrivacyMutation(
        () => this._privacyRepository.setTelemetryConsent(telemetry.checked),
      );
      return;
    }
    const discord = event.target?.closest?.(MARKERS.discordImages);
    if (discord) {
      void this._applyPrivacyMutation(
        () => this._privacyRepository.setDiscordImageConsent(discord.checked),
      );
    }
  }
}

export function registerConsentCenter() {
  if (typeof globalThis.customElements === "undefined") return;
  if (!globalThis.customElements.get(CONSENT_CENTER_TAG)) {
    globalThis.customElements.define(CONSENT_CENTER_TAG, FishyConsentCenter);
  }
}
